import React from 'react';
import { Button, Card } from '../ui';
import { ToggleRow } from './ToggleRow';

type CtaImageCardProps = {
  ctaFileInputRef: React.RefObject<HTMLInputElement>;
  handleCtaFileSelect: (_e: React.ChangeEvent<HTMLInputElement>) => void;
  ctaImageUrl: string;
  ctaPrompt: string;
  setCtaModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  ctaGenerating: boolean;
  ctaUploading: boolean;
  ctaDeleting: boolean;
  handleCtaDelete: () => void;
  ctaIncludeByDefault: boolean;
  handleCtaToggleDefault: (_value: boolean) => void;
};

export function CtaImageCard({
  ctaFileInputRef,
  handleCtaFileSelect,
  ctaImageUrl,
  ctaPrompt,
  setCtaModalOpen,
  ctaGenerating,
  ctaUploading,
  ctaDeleting,
  handleCtaDelete,
  ctaIncludeByDefault,
  handleCtaToggleDefault,
}: CtaImageCardProps) {
  return (
    <Card elevation="raised">
      <Card.Header>
        <Card.Title subtitle="A reusable closing image (e.g. “Follow for more”) appended as the last slide of image and carousel posts.">
          <span className="settings-section-icon">📣</span> Sign-off / CTA Image
        </Card.Title>
      </Card.Header>
      <Card.Body className="settings-card-body">
        <input
          ref={ctaFileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          style={{ display: 'none' }}
          onChange={handleCtaFileSelect}
        />
        <div className="settings-cta-row">
          <div className="settings-cta-preview">
            {ctaImageUrl ? (
              <img src={ctaImageUrl} alt="CTA sign-off" className="settings-cta-preview__img" />
            ) : (
              <div className="settings-cta-preview__empty">
                <span style={{ fontSize: 28 }}>📣</span>
                <span>No image yet</span>
              </div>
            )}
          </div>
          <div className="settings-cta-info">
            <p className="settings-cta-info__title">
              {ctaImageUrl ? 'Your sign-off image is ready' : 'Add a closing card to your posts'}
            </p>
            <p className="settings-cta-info__desc">
              When enabled, this image is auto-appended as the LAST slide of every image and
              carousel post. You can still opt in or out per post in the post preview.
            </p>
            {ctaPrompt && (
              <p className="settings-cta-info__prompt" title={ctaPrompt}>
                <strong>Last prompt:</strong> {ctaPrompt}
              </p>
            )}
            <div className="settings-cta-actions">
              <Button
                variant="primary"
                size="sm"
                onClick={() => setCtaModalOpen(true)}
                loading={ctaGenerating}
                disabled={ctaUploading || ctaDeleting}
              >
                {ctaImageUrl ? '✨ Regenerate with AI' : '✨ Generate with AI'}
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => ctaFileInputRef.current?.click()}
                loading={ctaUploading}
                disabled={ctaGenerating || ctaDeleting}
              >
                📁 {ctaImageUrl ? 'Replace upload' : 'Upload image'}
              </Button>
              {ctaImageUrl && (
                <Button
                  variant="danger"
                  size="sm"
                  onClick={handleCtaDelete}
                  loading={ctaDeleting}
                  disabled={ctaGenerating || ctaUploading}
                >
                  Remove
                </Button>
              )}
            </div>
          </div>
        </div>

        <ToggleRow
          label="Include on every image / carousel post"
          description={
            ctaImageUrl
              ? 'When on, every new image or carousel post adds this image as the last slide. You can still toggle it off per post.'
              : 'Add a CTA image first, then enable this to auto-include it on new posts.'
          }
          checked={ctaIncludeByDefault && Boolean(ctaImageUrl)}
          onChange={handleCtaToggleDefault}
          disabled={!ctaImageUrl}
        />
      </Card.Body>
    </Card>
  );
}
